// src/context/AdminAuthContext.tsx
import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';

interface AdminInfo {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

interface AdminAuthContextType {
  isAdminAuthenticated: boolean;
  admin: AdminInfo | null;
  adminLogin: (token: string, admin: AdminInfo) => void;
  adminLogout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);
  const [admin, setAdmin] = useState<AdminInfo | null>(null);

  useEffect(() => {
    // Restore admin session from localStorage
    const token = localStorage.getItem('adminToken');
    const stored = localStorage.getItem('adminInfo');
    if (token && stored) {
      try {
        // Check if token is expired (decode JWT payload)
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setIsAdminAuthenticated(true);
          setAdmin(JSON.parse(stored));
        } else {
          // Token expired, clean up
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminInfo');
        }
      } catch {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminInfo');
      }
    }
  }, []);

  const adminLogin = (token: string, adminInfo: AdminInfo) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminInfo', JSON.stringify(adminInfo));
    setIsAdminAuthenticated(true);
    setAdmin(adminInfo);
  };

  const adminLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    setIsAdminAuthenticated(false);
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider
      value={{ isAdminAuthenticated, admin, adminLogin, adminLogout }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export { AdminAuthContext };
